import React, { useRef, useState } from "react";
import { Alert, Button, Col, message, Modal, Progress, Row, Steps } from "antd";
import { UrlParams } from "../../../../../reviewExam";
import { ExamPDF, ExamPDFInstance } from "../ExamPDF";
import {
    ExamPaperItem,
    getExamInfoByStu
} from "../../../../../../../service/exam";
import { isTrue } from "../../../../../../../service/service";
import styles from "../../../../../../homework/review/component/DownloadBtn/Components/PaperExportModal/index.module.scss";
import { useFileSystem } from "../../../../../../../utils/useFileSystem";
import { ModalProps } from "antd/es/modal";

interface ExamExportModalProps extends ModalProps {
    name: string;
    data: ExamPaperItem[];
}

export function ExamExportModal({
    name,
    data,
    ...props
}: ExamExportModalProps) {
    const fileSystem = useFileSystem();
    const pdfRef = useRef<ExamPDFInstance>(null);

    const [errCount, setErrCount] = useState(0);
    const [index, setIndex] = useState(-1);

    const current = data[index];

    const percent =
        data.length === 0 ? 100 : Math.floor(((index + 1) * 100) / data.length);

    const finished = percent === 100;

    const [isStart, setIsStart] = useState(false);

    async function onStart() {
        await fileSystem.init();
        doDownload();
    }

    function onReset() {
        doDownload();
        setErrCount(0);
        setIndex(-1);
    }

    async function doDownload() {
        const dir = await fileSystem.createDir(name);

        setIsStart(true);

        for (const item of data) {
            try {
                fileSystem.checkAlive();
                setIndex(index => index + 1);
                await exportExamForStudent(dir, item);
            } catch (e) {
                if (e?.code === "cancel") return;
                setErrCount(count => count + 1);
                message.error(`导出错误:${item.name}`);
            }
        }
    }

    async function exportExamForStudent(
        rootDir: FileSystemDirectoryHandle,
        item: ExamPaperItem
    ) {
        const dir = await rootDir.getDirectoryHandle(
            `${item.name}${item.studentNo}`,
            {
                create: true
            }
        );

        // 已经完成
        if (await fileSystem.isFileExist(".done", dir)) return;

        if (!isTrue(item.studnetSubmit)) {
            await fileSystem.createFile("空白试卷.txt", dir);
            return;
        }

        await downLoadExam(dir, {
            paperId: item.paperId,
            examId: item.examId,
            studentId: item.studentId,
            ban: "0"
        });

        // 打包下载补传的附件
        await downloadExtraFiles(dir, item.examId, item.studentNo);

        await fileSystem.createFile(".done", dir);
    }

    async function downLoadExam(
        dirHandle: FileSystemDirectoryHandle,
        params: UrlParams
    ) {
        if (!pdfRef.current) return;
        await pdfRef.current?.load(params);

        const pdfBlob = await pdfRef.current?.getBlob();
        if (!pdfBlob) return;

        await fileSystem.saveBlob("试卷.pdf", pdfBlob, dirHandle);

        const files = pdfRef.current.getFiles();
        await fileSystem.downloadFiles(files, dirHandle);
    }

    // 下载老师补传的附件
    async function downloadExtraFiles(
        dirHandle: FileSystemDirectoryHandle,
        examId: string,
        studentNo: string
    ) {
        const data = await getExamInfoByStu(examId, studentNo);
        const files = data.allExtraFile?.map(item => item.fileName) || [];
        await fileSystem.downloadFiles(files, dirHandle);
    }

    return (
        <Modal
            {...props}
            title="打包下载"
            width={600}
            footer={false}
            destroyOnClose
            maskClosable={false}>
            <Row>
                <Col span={4} offset={2}>
                    步骤
                </Col>

                <Col span={16}>
                    <Steps
                        className={styles.steps}
                        current={isStart ? 2 : 0}
                        progressDot
                        status={finished ? "finish" : "wait"}
                        size="small">
                        <Steps.Step title="点击开始" />
                        <Steps.Step title="选择本地存储路径" />
                        <Steps.Step title="等待下载" />
                    </Steps>
                </Col>
            </Row>

            <Row style={{ marginTop: 20 }}>
                <Col span={4} offset={2}>
                    进度
                </Col>

                <Col span={16}>
                    <Progress percent={percent} />
                </Col>
            </Row>

            {!!errCount && (
                <Row style={{ marginTop: 20 }}>
                    <Col offset={2} span={4}>
                        错误数
                    </Col>

                    <Col>{errCount}</Col>
                </Row>
            )}

            <Row style={{ marginTop: 20 }}>
                <Col offset={2}>
                    说明：
                    <br />
                    1、
                    <span style={{ color: "red" }}>
                        下载过程中请不要关闭该窗口
                    </span>
                    ，提示下载完成后关闭即可。
                    <br />
                    2、下载过程中保持网络畅通，如提示有出错信息，重新下载即可。
                    <br />
                    3、可能出错的原因及处理办法：
                    <br />
                    （1）断网，请重新下载；
                    <br />
                    （2）本地保存路径文件夹为只读，重新选择下载路径；
                    <br />
                    （3）下载过程磁盘空间满，重新选择下载路径。
                </Col>
            </Row>

            {!finished && (
                <Button
                    style={{ marginTop: 20 }}
                    type="primary"
                    block
                    loading={isStart}
                    onClick={onStart}>
                    {isStart ? `正在下载 ${current?.name || ""}` : "开始下载"}
                </Button>
            )}

            {finished && (
                <Alert
                    style={{ marginTop: 20 }}
                    message={
                        <div>
                            下载完成 {errCount}个错误{" "}
                            <Button
                                type="link"
                                onClick={onReset}
                                hidden={!errCount}>
                                重试
                            </Button>
                        </div>
                    }
                    type="success"
                />
            )}

            {/*{fileSystem && !!data && index !== -1 && (*/}
            {/*    <TaskList*/}
            {/*        examName={name}*/}
            {/*        currentIndex={index}*/}
            {/*        data={data}*/}
            {/*        fileSystem={fileSystem}*/}
            {/*    />*/}
            {/*)}*/}

            <ExamPDF ref={pdfRef} />
        </Modal>
    );
}
